import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleXmark, faCartShopping, faHeart, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { useWishlistStore } from "../store/slice/wishlistSlice";
import { useCartStore } from "../store/slice/cartSlice";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

const Wishlist = () => {
  const { wishlist, removeFromWishlist } = useWishlistStore();
  const { addToCart, removeFromCart, cart } = useCartStore();
  const navigate = useNavigate();

  // Function to check if product is in cart
  const isInCart = (productId) => cart.some(item => item._id === productId);

  // Format price to currency style
  const formatPrice = (price) => {
    return `₹ ${price.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
  };

  if (wishlist.length === 0) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-16">
        <div className="text-center">
          <div className="mb-6">
            <FontAwesomeIcon icon={faHeart} className="text-6xl text-gray-300" />
          </div>
          <h2 className="text-3xl font-semibold mb-4 text-gray-800">
            Your wishlist is empty
          </h2>
          <p className="text-gray-600 mb-8">
            Looks like you haven't added anything to your wishlist yet.
          </p>
          <button
            onClick={() => navigate("/products")}
            className="mt-6 inline-block bg-black text-white px-8 py-3 rounded-md hover:bg-gray-800 transition"
          > 
            Browse Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Wishlist
          </h1>
          <p className="text-gray-600">Items you've saved for later</p>
        </div>

        {/* Wishlist Items Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {wishlist.map((product) => {
            const productInCart = isInCart(product._id);
            const totalStock = product.variation[0]?.color.reduce((sum, color) => {
              return sum + (Number(color.stock) || 0);
            }, 0) || 0;
            
            return (
              <div
                key={product._id}
                className="bg-white rounded-lg overflow-hidden flex flex-col max-w-xs mx-auto"
              >
                {/* Image Container with buttons overlay */}
                <div className="relative">
                  <img
                    src={product.images[0]}
                    alt={product.name}
                    className="w-full aspect-[250/220] object-cover cursor-pointer"
                    onClick={() => navigate(`/product/${product._id}`)}
                  />
                  
                  {/* Action buttons */}
                  <div className="absolute bottom-0 left-0 right-0 flex justify-center transform translate-y-1/2 gap-2">
                    {/* Cart button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (productInCart) {
                          removeFromCart(product._id);
                          toast.success(`${product.name} removed from cart`);
                        } else if (totalStock > 0) {
                          addToCart(product);
                          toast.success(`${product.name} added to cart`);
                        }
                      }}
                      disabled={totalStock === 0 && !productInCart}
                      className={`w-10 h-10 rounded-full flex items-center justify-center
                        transition-colors duration-200 shadow-md ${
                          productInCart 
                            ? "bg-red-500 hover:bg-red-600" 
                            : "bg-teal-600 hover:bg-teal-700"
                        }`}
                      aria-label={productInCart ? "Remove from cart" : "Add to cart"}
                    >
                      <FontAwesomeIcon 
                        icon={productInCart ? faTrashAlt : faCartShopping} 
                        className="text-white" 
                      />
                    </button>
                    
                    {/* Remove from wishlist button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFromWishlist(product._id);
                        toast.success(`${product.name} removed from wishlist`);
                      }}
                      className="w-10 h-10 rounded-full bg-gray-700 hover:bg-gray-800 flex items-center justify-center
                        transition-colors duration-200 shadow-md"
                      aria-label="Remove from wishlist"
                    >
                      <FontAwesomeIcon 
                        icon={faTrashAlt} 
                        className="text-white" 
                      />
                    </button>
                  </div>
                </div>

                {/* Product Info */}
                <div className="px-4 py-4 pt-6 flex flex-col items-center">
                  <h3 className="text-gray-700 font-medium text-center mb-1">
                    {product.name}
                  </h3>
                  <p className="text-xs text-gray-500 mb-2 line-clamp-2 text-center">
                    {product.description}
                  </p>
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-red-400 font-medium">
                      {formatPrice(product.price * (1 - product.discount / 100))}
                    </span>
                    
                    {product.discount > 0 && (
                      <span className="text-gray-500 font-medium line-through">
                        {formatPrice(product.price)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Wishlist;
